import {
  datamuseQuery,
  definitionFor,
  PARTS_OF_SPEECH,
  type PartOfSpeech,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";

/**
 * The Reverse Dictionary's one question: Datamuse's "means like" (`ml`) search with a
 * whole description as the query — in Datamuse's words, effectively OneLook's reverse
 * dictionary — plus an optional `topics` hint, at most five words, which re-ranks the
 * answer toward a subject rather than filtering it. A "starts with" constraint (`sp`)
 * keeps only words that begin with the letters given — the detail people most often
 * recall about a word on the tip of the tongue.
 */

const MAX_RESULTS = 80;
export const MAX_TOPICS = 5;
const RE_TOPIC_SPLIT = /[\s,]+/;
const RE_NOT_LETTER = /[^\p{L}'-]/gu;

/** The topic box as Datamuse wants it: up to five distinct lower-case words. */
export function topicWords(input: string): string[] {
  const words: string[] = [];
  for (const word of input.toLowerCase().split(RE_TOPIC_SPLIT)) {
    if (word && !words.includes(word)) {
      words.push(word);
    }
  }
  return words.slice(0, MAX_TOPICS);
}

/** The "starts with" box, reduced to the letters a spelling pattern can use. */
export function startLetters(input: string): string {
  return input.toLowerCase().replace(RE_NOT_LETTER, "");
}

export function reverseQuery(
  description: string,
  topics: string,
  startsWith = ""
): string {
  const hint = topicWords(topics);
  const start = startLetters(startsWith);
  return datamuseQuery({
    ml: description,
    topics: hint.length > 0 ? hint.join(",") : undefined,
    sp: start ? `${start}*` : undefined,
    md: "dpf",
    max: MAX_RESULTS,
  });
}

export interface ReverseEntry {
  word: string;
  pos: PartOfSpeech;
  definition: string | null;
}

/** Results in Datamuse's order, minus repeats and the description itself. */
export function reverseEntries(
  description: string,
  results: readonly WordResult[]
): ReverseEntry[] {
  const seen = new Set([description.toLowerCase()]);
  const entries: ReverseEntry[] = [];
  for (const result of results) {
    const key = result.word.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    const pos = result.pos[0] ?? "u";
    entries.push({
      word: result.word,
      pos,
      definition: definitionFor(result, pos)?.text ?? null,
    });
  }
  return entries;
}

/** The parts of speech present, in a fixed order, with how many entries each has. */
export function posCounts(
  entries: readonly ReverseEntry[]
): { pos: PartOfSpeech; count: number }[] {
  return PARTS_OF_SPEECH.map((pos) => ({
    pos,
    count: entries.filter((entry) => entry.pos === pos).length,
  })).filter((item) => item.count > 0);
}
